import { computed, provide, type ComputedRef } from "vue";

import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord
} from "@mcwd/util-vue";

import { useFormSettings } from './use-form-settings.js';
import { useGatingLevel } from './use-gating-level.js';
import { useFormSubmit } from './use-form-submit.js';

import { useFormLogger } from "./use-form-logger.js";

type UseBlindSubmitInjectionValues = InjectionValuesRecord<{
  blindSubmitEnabled: boolean;
  isBlindSubmitForm: ComputedRef<boolean>;
  submitBlindFormIfEnabled: () => void;
}>;

type UseBlindSubmitInjectionKeys = InjectionKeysRecord<UseBlindSubmitInjectionValues>;

const UseBlindSubmitInjectionKeys = CreateInjectionKeysRecord<UseBlindSubmitInjectionKeys>({
  blindSubmitEnabled: "blindSubmitEnabled",
  isBlindSubmitForm: "isBlindSubmitForm",
  submitBlindFormIfEnabled: "submitBlindFormIfEnabled",
});


export function initBlindSubmit() {
  if (checkValuesAlreadyProvided(UseBlindSubmitInjectionKeys)) {
    return;
  }
  const { FormSettings } = useFormSettings({ callerLabel: "use-blind-submit" });
  const blindSubmitEnabled = FormSettings.allowedGatingStates.includes("BlindSubmit");
  const { CurrentFormExperienceGatingState } = useGatingLevel({ callerLabel: "use-blind-submit" });

  const isBlindSubmitForm = computed(() => {
    return blindSubmitEnabled && CurrentFormExperienceGatingState.value === "BlindSubmit";
  });

  const { submitForm, errorCheck } = useFormSubmit({ callerLabel: "blind-submit" });

  const blindFormSettingsCheck = () => {
    if (!blindSubmitEnabled) {
      throw new Error(
        `Blind Submit does not exist for formExperience '${FormSettings.formSettingsKey}'`
      );
    }
    if (isBlindSubmitForm.value === false) {
      throw new Error(`Form is not in the BlindSubmit gating state. FormSettingsKey: ${FormSettings.formSettingsKey}`);
    }
  };
    const { logger } = useFormLogger();

  const submitBlindForm = () => {
    try {
      blindFormSettingsCheck();
      errorCheck();
      submitForm();
    }
    catch (err) {
      logger.error(`Error in submitBlindForm. Did you mean to use submitBlindFormIfEnabled?`);
      throw err;
    }
  };

  const submitBlindFormIfEnabled = () => {
    if (isBlindSubmitForm.value) {
      submitBlindForm();
    }
  };
  provide(UseBlindSubmitInjectionKeys.blindSubmitEnabled, false);
  provide(UseBlindSubmitInjectionKeys.isBlindSubmitForm, isBlindSubmitForm);
  provide(UseBlindSubmitInjectionKeys.submitBlindFormIfEnabled, submitBlindFormIfEnabled);
}

export function useBlindSubmit() {
  const blindSubmitEnabled = injectRequiredWithSelf(UseBlindSubmitInjectionKeys.blindSubmitEnabled);
  const isBlindSubmitForm = injectRequiredWithSelf(UseBlindSubmitInjectionKeys.isBlindSubmitForm);
  const submitBlindFormIfEnabled = injectRequiredWithSelf(UseBlindSubmitInjectionKeys.submitBlindFormIfEnabled); 
  return {
    blindSubmitEnabled,
    isBlindSubmitForm,
    submitBlindFormIfEnabled,
  };
}